import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  DialogProps,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import LoadingBar from "../LoadingBar/LoadingBar";
import { PropsWithChildren } from "react";
import { useModalHooks } from "../../State/Hooks/useModalHooks";

interface IModalProps extends DialogProps {
  title: string;
  hasLoadingBar: boolean;
  hasCloseButton?: boolean;
  children: any;
  isProcessing?: boolean;
  handleClose?: any;
}

/**
 * @description A reusable Modal component
 *
 * @param title The title of the Modal
 * @param hasLoadingBar Whether or not the modal should include the loading bar
 * @param children The React components you want to include in the modal
 * @param isProcessing Optional process that will trigger the loading bar
 * @param handleClose Optional function that is ran when the modal is closed. If not included the modal will
 * close without any of its children elements being affected.
 * @returns An empty Modal component
 */
export default function Modal(props: PropsWithChildren<IModalProps>) {
  const {
    title,
    hasLoadingBar,
    hasCloseButton = true,
    children,
    isProcessing,
    handleClose,
    ...rest
  } = props;

  const { closeModal } = useModalHooks();

  return (
    <Dialog onClose={handleClose ?? closeModal} {...rest}>
      {hasLoadingBar ? <LoadingBar loading={isProcessing} /> : null}
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{title}</span>
          {hasCloseButton ? (
            <IconButton
              data-test="close-dialog"
              aria-label="Close Dialog"
              onClick={handleClose ?? closeModal}
            >
              <Close />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
}
